import React, { useState, useEffect, useCallback } from 'react'
import Gallery from 'react-photo-gallery'
import { ReactComponent as PlayIcon } from 'shared/assets/icons/play.svg'
import { useMediaQuery } from '@react-hook/media-query'

// Components
import Modal from 'shared/components/Modal'
import Carousel from 'shared/components/Carousel'

import './styles.scss'
import { getCdnAssetUrl } from 'shared/helpers/assets'
import Loader from 'shared/components/Loader'
import { useStore } from 'TourApp/store'

const ESCAPE_KEY_CODE = 27

export default function MosaicView({
  items,
  className = '',
  onEnlargeImage,
  onSlideChange,
  onCloseImage,
  onEnlargeImageView,
  onVideoEvent,
  onVideoOver50PercentEvent,
}) {
  const { sidebar } = useStore()
  const [initialSlide, setInitialSlide] = useState(0)
  const [showModal, toggleModal] = useState(false)
  const [carouselInstance, setCarouselInstance] = useState(null)
  const [videoId, setVideoId] = useState(null)
  const [currentVideoDuration, setCurrentVideoDuration] = useState(null)
  const [videoPlayedOver50PercentEventSent, setVideoPlayedOver50PercentEventSent] = useState(false)
  const [loaded, setLoaded] = useState(0)
  const [galleryShown, setGalleryShown] = useState(0)
  const isMobile = useMediaQuery('(max-width: 767px)')

  const openImg = (_, { index, photo }) => {
    toggleModal(true)
    setInitialSlide(index)
    onEnlargeImage(photo)
    if (photo.isVideo) {
      setTimeout(() => {
        const video = document.getElementById(`video_${photo.id}`)
        video?.play()
      }, 100)
    }
  }

  const handleKeyDown = useCallback(
    (e) => {
      if (e.keyCode === ESCAPE_KEY_CODE) {
        carouselInstance?.activeIndex && onCloseImage(carouselInstance.activeIndex)
        toggleModal(false)
      }
    },
    [carouselInstance, onCloseImage],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useEffect(() => {
    // it works only once
    if (carouselInstance?.activeIndex !== undefined) {
      onEnlargeImageView(carouselInstance)
    }
  }, [carouselInstance, carouselInstance?.activeIndex, onEnlargeImageView])

  function normalizeId(id) {
    return id.split('_')[1] || id
  }

  function onVideoPlay(e) {
    setVideoPlayedOver50PercentEventSent(false)
    setCurrentVideoDuration(e.currentTarget.duration)
    const { id, src: videoSrc } = e.target
    setVideoId(id)
    onVideoEvent({ id: normalizeId(id), videoSrc, isVideo: true })
  }

  function onVideoEnd(e) {
    setVideoPlayedOver50PercentEventSent(false)
    const { id, src: videoSrc } = e.target
    onVideoEvent({ id: normalizeId(id), videoSrc, isVideo: true }, true)
    e.target.currentTime = 0
    e.target?.webkitExitFullscreen()
  }

  function changeSlide(item) {
    const v = document.getElementById(videoId)
    v?.pause()
    onSlideChange(item)
  }

  function onVideoPlayProgress({ currentTime }) {
    const currentlyPlayedPercentage = Math.round((currentTime * 100) / currentVideoDuration)
    const { id, src: videoSrc } = document.getElementById(videoId)
    if (currentlyPlayedPercentage > 50 && !videoPlayedOver50PercentEventSent) {
      onVideoOver50PercentEvent({ id: normalizeId(id), videoSrc, isVideo: true })
      setVideoPlayedOver50PercentEventSent(true)
    }
  }

  const imageRenderer = ({ index, photo, margin, onClick, ...p }) => (
    <div
      className="relative cursor-pointer"
      index={index}
      style={{ margin, height: photo.height, width: photo.width }}
      onClick={(e) => onClick(e, { index, photo })}
      {...p}
    >
      <img
        alt={photo.title}
        src={getCdnAssetUrl(photo.src)}
        className="w-full h-full"
        onLoad={() => {
          setLoaded((loaded) => loaded + 1)
        }}
        onError={() => {
          setLoaded((loaded) => loaded + 1)
        }}
      />
      {photo.isVideo && <PlayIcon className="absolute h-[45%] top-[calc(30%)] left-[calc(50%-25px)]" />}
    </div>
  )

  useEffect(() => {
    if (loaded >= items.length) {
      setTimeout(() => {
        setGalleryShown(true)
      }, 500)
    }
  }, [loaded, items])

  return (
    <>
      {!galleryShown && (
        <div className="p-5 mt-[32px]">
          <Loader />
        </div>
      )}
      <div
        className={`w-full h-full overflow-x-auto py-[32px] px-[16px] lg:p-0 ${className} ${
          galleryShown ? 'opacity-1' : 'opacity-0'
        }`}
      >
        <Gallery
          photos={items}
          onClick={openImg}
          margin={isMobile ? 6 : 12}
          targetRowHeight={isMobile ? 115 : 260}
          renderImage={imageRenderer}
        />
      </div>
      {showModal && (
        <Modal
          className={`lg:bottom-[30px] transition-all ease-out duration-300 ${sidebar.minimized ? 'lg:left-[56px]' : 'lg:left-[336px]'}`}
          toggleModal={(e) => {
            onCloseImage(carouselInstance.activeIndex)
            toggleModal(e)
          }}
          showCloseBtn
          closeAnywhere
        >
          <div className="h-full">
            <Carousel
              navigation
              slidesPerView={1}
              initialSlide={initialSlide}
              onSlideChange={changeSlide}
              onTransitionEnd={onEnlargeImageView}
              onSwiper={setCarouselInstance}
              items={items.map((img, i) =>
                img.isVideo ? (
                  <video
                    key={i}
                    id={`video_${img.id}`}
                    className="media object-contain cursor-move w-full h-full"
                    src={getCdnAssetUrl(img.videoSrc)}
                    poster={getCdnAssetUrl(img.thumbnail)}
                    controls
                    controlsList="nodownload"
                    onPlay={onVideoPlay}
                    onEnded={onVideoEnd}
                    onTimeUpdate={(e) => {
                      onVideoPlayProgress(e.currentTarget)
                    }}
                  />
                ) : (
                  <img
                    className="media object-contain cursor-move w-full h-full"
                    src={getCdnAssetUrl(img.src)}
                    key={img.id}
                    alt=""
                  />
                ),
              )}
            />
          </div>
        </Modal>
      )}
    </>
  )
}
